import * as React from 'react';
import { PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/solid';

interface ScheduleItem {
  id: string;
  day: string;
  startTime: string;
  endTime: string;
  grade: string;
  locationId: string; // Reference to center ID
}

interface Center {
  id: string;
  name: string;
  address: string;
  locationLink: string;
}

const DAYS_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const GRADES = [
  'Grade 1',
  'Grade 2',
  'Grade 3',
  'Grade 4',
  'Grade 5',
  'Grade 6',
  'Grade 7',
  'Grade 8',
  'Grade 9',
  'Grade 10',
  'Grade 11',
  'Grade 12',
];

const Schedule: React.FC = () => {
  const [scheduleItems, setScheduleItems] = React.useState<ScheduleItem[]>([]);
  const [centers, setCenters] = React.useState<Center[]>([]);
  const [newScheduleItem, setNewScheduleItem] = React.useState<Omit<ScheduleItem, 'id'>>({  
    day: DAYS_OF_WEEK[0],
    startTime: '08:00',
    endTime: '09:00',
    grade: GRADES[0],
    locationId: '',
  });
  const [newCenter, setNewCenter] = React.useState<Omit<Center, 'id'>>({  
    name: '',
    address: '',
    locationLink: '',
  });
  const [selectedGradeFilter, setSelectedGradeFilter] = React.useState<string>('All');
  const [showAddCenter, setShowAddCenter] = React.useState(false);
  const [showAddScheduleItem, setShowAddScheduleItem] = React.useState(false);

  // Load schedule items and centers from localStorage on component mount
  React.useEffect(() => {
    const savedSchedule = localStorage.getItem('schedule');
    if (savedSchedule) {
      setScheduleItems(JSON.parse(savedSchedule));
    }
    const savedCenters = localStorage.getItem('centers');
    if (savedCenters) {
      setCenters(JSON.parse(savedCenters));
    }
  }, []);

  // Save schedule items and centers to localStorage whenever they change
  React.useEffect(() => {
    localStorage.setItem('schedule', JSON.stringify(scheduleItems));
  }, [scheduleItems]);

  React.useEffect(() => {
    localStorage.setItem('centers', JSON.stringify(centers));
  }, [centers]);

  const handleScheduleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewScheduleItem(prev => ({ ...prev, [name]: value }));
  };

  const handleCenterInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewCenter(prev => ({ ...prev, [name]: value }));
  };

  const handleAddCenter = () => {
    if (!newCenter.name.trim()) {
      alert('Please enter a center name');
      return;
    }

    const newItem: Center = {
      ...newCenter,
      id: Date.now().toString(),
    };

    setCenters(prev => [...prev, newItem]);
    setNewCenter({
      name: '',
      address: '',
      locationLink: '',
    });
    setShowAddCenter(false);
  };

  const handleAddScheduleItem = () => {
    if (!newScheduleItem.locationId) {
      alert('Please select a location');
      return;
    }

    const newItem: ScheduleItem = {
      ...newScheduleItem,
      id: Date.now().toString(),
    };

    setScheduleItems(prev => [...prev, newItem]);
    setNewScheduleItem({
      day: DAYS_OF_WEEK[0],
      startTime: '08:00',
      endTime: '09:00',
      grade: GRADES[0],
      locationId: '',
    });
    setShowAddScheduleItem(false);
  };

  const handleDeleteScheduleItem = (id: string) => {
    setScheduleItems(prev => prev.filter(item => item.id !== id));
  };

  const handleDeleteCenter = (id: string) => {
    // First check if this center is being used in any schedule items
    const isUsed = scheduleItems.some(item => item.locationId === id);
    if (isUsed) {
      alert('This center is being used in schedule items. Please remove those items first.');
      return;
    }
    setCenters(prev => prev.filter(center => center.id !== id));
  };

  const getCenterName = (locationId: string) => {
    const center = centers.find(c => c.id === locationId);
    return center ? center.name : 'Unknown';
  };

  const filteredScheduleItems = selectedGradeFilter === 'All'
    ? scheduleItems
    : scheduleItems.filter(item => item.grade === selectedGradeFilter);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Teacher Schedule</h1>
      
      {/* Action Buttons */}
      <div className="mb-6 flex space-x-4">
        <button
          onClick={() => setShowAddCenter(true)}
          className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Center
        </button>
        
        <button
          onClick={() => setShowAddScheduleItem(true)}
          disabled={centers.length === 0}
          className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Schedule Item
        </button>
        {centers.length === 0 && (
          <p className="text-sm text-gray-500 self-center">Please add a center first before adding schedule items.</p>
        )}
      </div>
      
      {/* Filter by Grade */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Grade</label>
        <select
          value={selectedGradeFilter}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedGradeFilter(e.target.value)}
          className="mt-1 block w-full md:w-64 rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
        >
          <option value="All">All Grades</option>
          {GRADES.map((grade) => (
            <option key={grade} value={grade}>{grade}</option>
          ))}
        </select>
      </div>

      {/* Add New Center Form (Modal) */}
      {showAddCenter && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add New Center</h3>
              <button 
                onClick={() => setShowAddCenter(false)} 
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Center Name</label>
                <input
                  type="text"
                  name="name"
                  value={newCenter.name}
                  onChange={handleCenterInputChange}
                  placeholder="Enter center name"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Center Address</label>
                <input
                  type="text"
                  name="address"
                  value={newCenter.address}
                  onChange={handleCenterInputChange}
                  placeholder="Enter address"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location Link</label>
                <input
                  type="text"
                  name="locationLink"
                  value={newCenter.locationLink}
                  onChange={handleCenterInputChange}
                  placeholder="https://maps.google.com/..."
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                />
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowAddCenter(false)}
                className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCenter}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
              >
                Add Center
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add New Schedule Item Form (Modal) */}
      {showAddScheduleItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add New Schedule Item</h3>
              <button 
                onClick={() => setShowAddScheduleItem(false)} 
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day</label>
                <select
                  name="day"
                  value={newScheduleItem.day}
                  onChange={handleScheduleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                <input
                  type="time"
                  name="startTime"
                  value={newScheduleItem.startTime}
                  onChange={handleScheduleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                <input
                  type="time"
                  name="endTime"
                  value={newScheduleItem.endTime}
                  onChange={handleScheduleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                <select
                  name="grade"
                  value={newScheduleItem.grade}
                  onChange={handleScheduleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                >
                  {GRADES.map((grade) => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <select
                  name="locationId"
                  value={newScheduleItem.locationId}
                  onChange={handleScheduleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                >
                  <option value="">Select a center</option>
                  {centers.map((center) => (
                    <option key={center.id} value={center.id}>{center.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowAddScheduleItem(false)}
                className="mr-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddScheduleItem}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
              >
                Add Schedule Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Centers List */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">Centers</h2>
        {centers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location Link
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {centers.map((center) => (
                  <tr key={center.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {center.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {center.address}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {center.locationLink ? (
                        <a 
                          href={center.locationLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          View on Map
                        </a>
                      ) : (
                        <span className="text-gray-400">No link provided</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleDeleteCenter(center.id)}
                        className="text-red-600 hover:text-red-900 focus:outline-none"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">No centers added yet. Add your first center!</p>
        )}
      </div>

      {/* Schedule Items Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <h2 className="text-lg font-semibold p-6 pb-0">Schedule Items</h2>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Day
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Time
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Grade
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredScheduleItems.length > 0 ? (
              filteredScheduleItems.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.day}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.startTime} - {item.endTime}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.grade}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getCenterName(item.locationId)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleDeleteScheduleItem(item.id)}
                      className="text-red-600 hover:text-red-900 focus:outline-none"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                  {selectedGradeFilter === 'All' ? 
                    'No schedule items found. Add your first one!' :
                    `No schedule items found for ${selectedGradeFilter}. Add one or change your filter.`
                  }
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Schedule;
